import { useState, useEffect, useCallback, useRef } from 'react';
import { BUILD_VERSION } from '../buildInfo';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export default function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') return;
    if (checking.current || updateAvailable) return;
    checking.current = true;
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.version && data.version !== BUILD_VERSION) {
        setUpdateAvailable(true);
      }
    } catch (_) {
      /* offline or transient network error — ignore, retry on next tick */
    } finally {
      checking.current = false;
    }
  }, [updateAvailable]);

  useEffect(() => {
    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  const reload = useCallback(() => {
    if ('caches' in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .finally(() => window.location.reload());
    } else {
      window.location.reload();
    }
  }, []);

  return { updateAvailable, reload };
}
